import React from 'react';
import Report from '@mui/icons-material/Report';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { userRequest } from '../../auth/requestMethods';
import { countSchoolDays } from '../../helper/countSchoolDays';
import { formatDateWithoutTime } from '../../helper/formatDate';
import Breadcrumb from '../common/breadcrumb';
import { AlertCircle } from 'react-feather';
import { Alert } from 'reactstrap';
import { Helmet } from 'react-helmet-async';

const DeliveryRunDetails = ({ location }) => {
  const { id: deliveryRunId } = useParams();

  const [currentSchoolId, setCurrentSchoolId] = useState('');
  const [currentSkuTypeId, setCurrentSkuTypeId] = useState('');
  const [deliveryRunDetails, setDeliveryRunDetails] = useState({});
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [terms, setTerms] = useState([]);
  const [skuTypes, setSkuTypes] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [menuSkuTypes, setMenuSkuTypes] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [skus, setSkus] = useState([]);

  function handleSkuTypeChange(event) {
    setCurrentSkuTypeId(event.target.value);
  }

  function handleSchoolChange(event) {
    setCurrentSchoolId(event.target.value);
  }

  function stepSchool(direction) {
    let current = schools.findIndex((obj) => obj.id === currentSchoolId);

    if (direction === 'next') {
      current = current + 1 < schools.length ? current + 1 : schools.length - 1;
      setCurrentSchoolId(schools[current].id);
    } else if (direction === 'prev') {
      current = current - 1 > 0 ? current - 1 : 0;
      setCurrentSchoolId(schools[current].id);
    }
  }

  function stepSku(direction) {
    let current = skuTypes.findIndex((obj) => obj.id === currentSkuTypeId);

    if (direction === 'next') {
      current = current + 1 < skuTypes.length ? current + 1 : skuTypes.length - 1;
      setCurrentSkuTypeId(skuTypes[current].id);
    } else if (direction === 'prev') {
      current = current - 1 > 0 ? current - 1 : 0;
      setCurrentSkuTypeId(skuTypes[current].id);
    }
  }

  useEffect(() => {
    setCurrentSkuTypeId('');
  }, [currentSchoolId]);

  useEffect(() => {
    if (deliveryRunId) {
      userRequest()
        .get(`/delivery-runs/retrieve/${deliveryRunId}`)
        .then(({ data }) => {
          if (data.deliveryRun) {
            setDeliveryRunDetails(data.deliveryRun);
          }
        })
        .catch((error) => console.log(error));

      userRequest()
        .get(`/delivery-runs/schools/${deliveryRunId}`)
        .then(({ data }) => {
          if (data.schools.length) {
            setCurrentSchoolId(data.schools[0].id);
            setSchools(data.schools);
          }
        })
        .catch((error) => console.log(error));

      userRequest()
        .get(`/delivery-runs/school-calendars/terms/${deliveryRunId}`)
        .then(({ data }) => {
          if (data.terms) {
            setTerms(data.terms);
          }
        })
        .catch((error) => console.log(error));

      userRequest()
        .get('/public-holidays')
        .then(({ data }) => {
          if (data.publicHolidays) {
            setPublicHolidays(data.publicHolidays);
          }
        })
        .catch((error) => console.log(error));
    }
  }, [deliveryRunId]);

  useEffect(() => {
    if (deliveryRunId && currentSchoolId) {
      userRequest()
        .get(`/delivery-runs/exceptions/${deliveryRunId}?school=${currentSchoolId}`)
        .then(({ data }) => {
          setExceptions(data.exceptions);

          setMenuSkuTypes(data.menuSkuTypes);

          let skuTypes = data.menuSkuTypes.map((obj) => {
            return { id: obj.sku_type_id, skuType: obj.sku_type };
          });

          skuTypes = skuTypes.reduce((acc, current) => {
            if (!acc.some((item) => item.id === current.id)) {
              acc.push(current);
            }
            return acc;
          }, []);

          setSkuTypes(skuTypes);
        })
        .catch((error) => console.log(error));
    }
  }, [currentSchoolId, deliveryRunId]);

  useEffect(() => {
    if (currentSkuTypeId) {
      userRequest()
        .get(`/delivery-runs/skus/${currentSkuTypeId}`)
        .then(({ data }) => {
          setSkus(data.skus);
          setConversions(data.conversions);
        })
        .catch((error) => console.log(error));
    }
  }, [currentSkuTypeId]);

  let commonUnits = new Set(skus.map((obj) => obj.unit_of_measure_id));

  const currentSchool = schools.find((obj) => obj.id === currentSchoolId);
  const actualMenu = {
    id: schools.find((obj) => obj.id === currentSchoolId)?.menu_id,
    name: schools.find((obj) => obj.id === currentSchoolId)?.menu_name,
  };

  // Create a list of menu objects for temp menus
  const tempMenuData = menuSkuTypes
    .filter((obj) => obj.sku_type_id === currentSkuTypeId && obj.menu_id !== actualMenu.id)
    .reduce((acc, current) => {
      if (!acc.some((item) => item.menuId === current.menu_id)) {
        acc.push({ menuId: current.menu_id, menuName: current.menu_name, exceptionId: current.exception_id });
      }
      return acc;
    }, [])
    .map((menu) => {
      const exception = exceptions.find((obj) => obj.exception_id === menu.exceptionId);
      const menuData = menuSkuTypes.filter(
        (obj) => menu.menuId === obj.menu_id && obj.sku_type_id === currentSkuTypeId
      );

      const startDate = new Date(
        Math.max(
          new Date(deliveryRunDetails.consumption_start_date),
          exception?.from_date ? new Date(exception.from_date) : new Date(deliveryRunDetails.consumption_start_date)
        )
      );

      const endDate = new Date(
        Math.min(
          new Date(deliveryRunDetails.consumption_end_date),
          exception?.to_date ? new Date(exception.to_date) : new Date(deliveryRunDetails.consumption_end_date)
        )
      );

      const formattedMenuData = [];

      const days = countSchoolDays(startDate, endDate, terms, publicHolidays).include;

      formattedMenuData.push({
        id: 1,
        rowName: 'Days',
        monday: days['1'] ? days['1'] : '',
        tuesday: days['2'] ? days['2'] : '',
        wednesday: days['3'] ? days['3'] : '',
        thursday: days['4'] ? days['4'] : '',
        friday: days['5'] ? days['5'] : '',
      });

      const monday = menuData.find((obj) => obj.day_of_week === 1);
      const tuesday = menuData.find((obj) => obj.day_of_week === 2);
      const wednesday = menuData.find((obj) => obj.day_of_week === 3);
      const thursday = menuData.find((obj) => obj.day_of_week === 4);
      const friday = menuData.find((obj) => obj.day_of_week === 5);

      const dayObjects = [monday, tuesday, wednesday, thursday, friday];

      formattedMenuData.push({
        id: 2,
        rowName: 'Menu Portion Quantity',
        monday: `${monday?.quantity_in_unit_of_measure ?? ''} ${monday?.display_unit ?? ''}`,
        tuesday: `${tuesday?.quantity_in_unit_of_measure ?? ''} ${tuesday?.display_unit ?? ''}`,
        wednesday: `${wednesday?.quantity_in_unit_of_measure ?? ''} ${wednesday?.display_unit ?? ''}`,
        thursday: `${thursday?.quantity_in_unit_of_measure ?? ''} ${thursday?.display_unit ?? ''}`,
        friday: `${friday?.quantity_in_unit_of_measure ?? ''} ${friday?.display_unit ?? ''}`,
      });

      formattedMenuData.push({
        id: 3,
        rowName: 'Total Portion Size',
        monday:
          days['1'] * (monday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(days['1'] * (monday?.quantity_in_unit_of_measure ?? 0), 3)} ${monday?.display_unit ?? ''}`
            : '',
        tuesday:
          days['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(days['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0), 3)} ${tuesday?.display_unit ?? ''}`
            : '',
        wednesday:
          days['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(days['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0), 3)} ${wednesday?.display_unit ?? ''}`
            : '',
        thursday:
          days['4'] * (thursday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(days['4'] * (thursday?.quantity_in_unit_of_measure ?? 0), 3)} ${thursday?.display_unit ?? ''}`
            : '',
        friday:
          days['5'] * (friday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(days['5'] * (friday?.quantity_in_unit_of_measure ?? 0), 3)} ${friday?.display_unit ?? ''}`
            : '',
      });

      // Initialize an empty array to store the unit objects
      const unitObjects = [];

      dayObjects.forEach((day, index) => {
        if (day) {
          const { quantity_in_unit_of_measure, display_unit, uom_id } = day;
          const multiplier = days[(index + 1).toString()];

          // Check if the unit of measurement already exists in unitObjects
          const existingUnitObject = unitObjects.find((obj) => obj.unit === display_unit);

          if (existingUnitObject) {
            // If the unit exists, update its total quantity
            existingUnitObject.totalQuantity += quantity_in_unit_of_measure * multiplier;
          } else {
            // If the unit doesn't exist, create a new object for it
            const newUnitObject = {
              unit: display_unit,
              totalQuantity: quantity_in_unit_of_measure * multiplier,
              uom_id: uom_id,
            };
            unitObjects.push(newUnitObject);
          }
        }
      });

      return {
        menuName: `Temporary Menu (${menu.menuName})`,
        total: { total: 0, displayUnit: null, uomId: null },
        menuBreakdown: formattedMenuData,
        seperatedUnits: unitObjects,
        days: days,
        type: 'Temp Menu',
      };
    });

  // Initialize main menu data =========================================================================================
  const menuData = menuSkuTypes.filter((obj) => obj.menu_id === actualMenu.id && obj.sku_type_id === currentSkuTypeId);

  const monday = menuData.find((obj) => obj.day_of_week === 1);
  const tuesday = menuData.find((obj) => obj.day_of_week === 2);
  const wednesday = menuData.find((obj) => obj.day_of_week === 3);
  const thursday = menuData.find((obj) => obj.day_of_week === 4);
  const friday = menuData.find((obj) => obj.day_of_week === 5);
  const dayObjects = [monday, tuesday, wednesday, thursday, friday];

  const mainMenuPortions = {
    id: 2,
    rowName: 'Menu Portion Quantity',
    monday: `${monday?.quantity_in_unit_of_measure ?? ''} ${monday?.display_unit ?? ''}`,
    tuesday: `${tuesday?.quantity_in_unit_of_measure ?? ''} ${tuesday?.display_unit ?? ''}`,
    wednesday: `${wednesday?.quantity_in_unit_of_measure ?? ''} ${wednesday?.display_unit ?? ''}`,
    thursday: `${thursday?.quantity_in_unit_of_measure ?? ''} ${thursday?.display_unit ?? ''}`,
    friday: `${friday?.quantity_in_unit_of_measure ?? ''} ${friday?.display_unit ?? ''}`,
  };

  const daysToRemove = tempMenuData.reduce(
    (acc, cur) => {
      return {
        1: acc['1'] + cur.days['1'],
        2: acc['2'] + cur.days['2'],
        3: acc['3'] + cur.days['3'],
        4: acc['4'] + cur.days['4'],
        5: acc['5'] + cur.days['5'],
      };
    },
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  );

  // Calculate the sku exeptions =======================================================================================

  const skuExeptions = exceptions.filter(
    (obj) => obj.type === 'MENU_SKU_TYPES' && obj.sku_type_id === currentSkuTypeId
  );
  for (const skuExep of skuExeptions) {
    //const skuExepStart = skuExep?.from_date ? new Date(skuExep.from_date) : new Date(-8640000000000000);
    //const skuExepEnd = skuExep?.to_date ? new Date(skuExep.to_date) : new Date(-8640000000000000);
    const skuExepStart = new Date(
      Math.max(
        new Date(skuExep?.from_date || deliveryRunDetails.consumption_start_date),
        new Date(deliveryRunDetails.consumption_start_date)
      )
    );
    const skuExepEnd = new Date(
      Math.min(
        new Date(skuExep?.to_date || deliveryRunDetails.consumption_end_date),
        new Date(deliveryRunDetails.consumption_end_date)
      )
    );
    const exceptionDays = countSchoolDays(skuExepStart, skuExepEnd, terms, publicHolidays).include;

    if (skuExep) {
      const skuExceptionMenuData = [];
      skuExceptionMenuData[0] = {
        id: 1,
        rowName: 'Days',
        monday: exceptionDays['1'] ? exceptionDays['1'] : '',
        tuesday: exceptionDays['2'] ? exceptionDays['2'] : '',
        wednesday: exceptionDays['3'] ? exceptionDays['3'] : '',
        thursday: exceptionDays['4'] ? exceptionDays['4'] : '',
        friday: exceptionDays['5'] ? exceptionDays['5'] : '',
      };

      skuExceptionMenuData[1] = mainMenuPortions;

      skuExceptionMenuData[2] = {
        id: 3,
        rowName: 'Total Portion Size',
        monday:
          exceptionDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(exceptionDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0), 3)} ${
                monday?.display_unit ?? ''
              }`
            : '',
        tuesday:
          exceptionDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(exceptionDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
                tuesday?.display_unit ?? ''
              }`
            : '',
        wednesday:
          exceptionDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(exceptionDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
                wednesday?.display_unit ?? ''
              }`
            : '',
        thursday:
          exceptionDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(exceptionDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0), 3)} ${
                thursday?.display_unit ?? ''
              }`
            : '',
        friday:
          exceptionDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0)
            ? `${round(exceptionDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0), 3)} ${
                friday?.display_unit ?? ''
              }`
            : '',
      };

      // Initialize an empty array to store the unit objects
      const unitObjects = [];

      dayObjects.forEach((day, index) => {
        if (day) {
          const { quantity_in_unit_of_measure, display_unit, uom_id } = day;
          const multiplier = exceptionDays[(index + 1).toString()];

          // Check if the unit of measurement already exists in unitObjects
          const existingUnitObject = unitObjects.find((obj) => obj.unit === display_unit);

          if (existingUnitObject) {
            // If the unit exists, update its total quantity
            existingUnitObject.totalQuantity += quantity_in_unit_of_measure * multiplier;
          } else {
            // If the unit doesn't exist, create a new object for it
            const newUnitObject = {
              unit: display_unit,
              totalQuantity: quantity_in_unit_of_measure * multiplier,
              uom_id: uom_id,
            };
            unitObjects.push(newUnitObject);
          }
        }
      });

      tempMenuData.unshift({
        menuName: `SKU Exception`,
        menuBreakdown: skuExceptionMenuData,
        seperatedUnits: unitObjects,
        total: { total: 0, displayUnit: undefined, uomId: undefined },
        days: exceptionDays,
        type: 'SKU Exception',
      });

      daysToRemove['1'] += exceptionDays['1'];
      daysToRemove['2'] += exceptionDays['2'];
      daysToRemove['3'] += exceptionDays['3'];
      daysToRemove['4'] += exceptionDays['4'];
      daysToRemove['5'] += exceptionDays['5'];
    }
  }
  // Calculate the learner exeptions ===================================================================================
  const learnerExeptions = exceptions.filter((obj) => obj.type === 'LEARNER_REDUCTION');

  for (const learnerExep of learnerExeptions) {
    const learnerExepStart = new Date(
      Math.max(
        new Date(learnerExep?.from_date || deliveryRunDetails.consumption_start_date),
        new Date(deliveryRunDetails.consumption_start_date)
      )
    );
    const learnerExepEnd = new Date(
      Math.min(
        new Date(learnerExep?.to_date || deliveryRunDetails.consumption_end_date),
        new Date(deliveryRunDetails.consumption_end_date)
      )
    );

    const exceptionDays = countSchoolDays(learnerExepStart, learnerExepEnd, terms, publicHolidays).include;

    const learnerExepData = [];
    learnerExepData[0] = {
      id: 1,
      rowName: 'Days',
      monday: exceptionDays['1'] ? exceptionDays['1'] : '',
      tuesday: exceptionDays['2'] ? exceptionDays['2'] : '',
      wednesday: exceptionDays['3'] ? exceptionDays['3'] : '',
      thursday: exceptionDays['4'] ? exceptionDays['4'] : '',
      friday: exceptionDays['5'] ? exceptionDays['5'] : '',
    };

    learnerExepData[1] = mainMenuPortions;

    learnerExepData[2] = {
      id: 3,
      rowName: 'Total Portion Size',
      monday:
        exceptionDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0)
          ? `${round(exceptionDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0), 3)} ${monday?.display_unit ?? ''}`
          : '',
      tuesday:
        exceptionDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0)
          ? `${round(exceptionDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
              tuesday?.display_unit ?? ''
            }`
          : '',
      wednesday:
        exceptionDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0)
          ? `${round(exceptionDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
              wednesday?.display_unit ?? ''
            }`
          : '',
      thursday:
        exceptionDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0)
          ? `${round(exceptionDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0), 3)} ${
              thursday?.display_unit ?? ''
            }`
          : '',
      friday:
        exceptionDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0)
          ? `${round(exceptionDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0), 3)} ${friday?.display_unit ?? ''}`
          : '',
    };

    // Initialize an empty array to store the unit objects
    const unitObjects = [];

    dayObjects.forEach((day, index) => {
      if (day) {
        const { quantity_in_unit_of_measure, display_unit, uom_id } = day;
        const multiplier = exceptionDays[(index + 1).toString()];

        // Check if the unit of measurement already exists in unitObjects
        const existingUnitObject = unitObjects.find((obj) => obj.unit === display_unit);

        if (existingUnitObject) {
          // If the unit exists, update its total quantity
          existingUnitObject.totalQuantity += quantity_in_unit_of_measure * multiplier;
        } else {
          // If the unit doesn't exist, create a new object for it
          const newUnitObject = {
            unit: display_unit,
            totalQuantity: quantity_in_unit_of_measure * multiplier,
            uom_id: uom_id,
          };
          unitObjects.push(newUnitObject);
        }
      }
    });

    tempMenuData.unshift({
      menuName: `Learner Reduction`,
      menuBreakdown: learnerExepData,
      seperatedUnits: unitObjects,
      total: { total: 0, displayUnit: undefined, uomId: undefined },
      days: exceptionDays,
      type: 'Learner Reduction',
      learnerCount: Math.round(
        (1 - learnerExep.temporary_learners_fraction / 100) * Number(currentSchool?.learner_count ?? 0)
      ),
    });

    daysToRemove['1'] += exceptionDays['1'];
    daysToRemove['2'] += exceptionDays['2'];
    daysToRemove['3'] += exceptionDays['3'];
    daysToRemove['4'] += exceptionDays['4'];
    daysToRemove['5'] += exceptionDays['5'];
  }

  // Calculate the actual menu =========================================================================================
  const startDate = new Date(deliveryRunDetails.consumption_start_date);
  const endDate = new Date(deliveryRunDetails.consumption_end_date);
  const days = countSchoolDays(startDate, endDate, terms, publicHolidays).include;

  const actualMenuDays = {
    1: days['1'] - daysToRemove['1'],
    2: days['2'] - daysToRemove['2'],
    3: days['3'] - daysToRemove['3'],
    4: days['4'] - daysToRemove['4'],
    5: days['5'] - daysToRemove['5'],
  };

  const formattedMenuData = [];
  formattedMenuData.push({
    id: 1,
    rowName: 'Days',
    monday: actualMenuDays['1'] ? actualMenuDays['1'] : '',
    tuesday: actualMenuDays['2'] ? actualMenuDays['2'] : '',
    wednesday: actualMenuDays['3'] ? actualMenuDays['3'] : '',
    thursday: actualMenuDays['4'] ? actualMenuDays['4'] : '',
    friday: actualMenuDays['5'] ? actualMenuDays['5'] : '',
  });
  formattedMenuData.push(mainMenuPortions);

  formattedMenuData.push({
    id: 3,
    rowName: 'Total Portion Size',
    monday:
      actualMenuDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0)
        ? `${round(actualMenuDays['1'] * (monday?.quantity_in_unit_of_measure ?? 0), 3)} ${monday?.display_unit ?? ''}`
        : '',
    tuesday:
      actualMenuDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0)
        ? `${round(actualMenuDays['2'] * (tuesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
            tuesday?.display_unit ?? ''
          }`
        : '',
    wednesday:
      actualMenuDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0)
        ? `${round(actualMenuDays['3'] * (wednesday?.quantity_in_unit_of_measure ?? 0), 3)} ${
            wednesday?.display_unit ?? ''
          }`
        : '',
    thursday:
      actualMenuDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0)
        ? `${round(actualMenuDays['4'] * (thursday?.quantity_in_unit_of_measure ?? 0), 3)} ${
            thursday?.display_unit ?? ''
          }`
        : '',
    friday:
      actualMenuDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0)
        ? `${round(actualMenuDays['5'] * (friday?.quantity_in_unit_of_measure ?? 0), 3)} ${friday?.display_unit ?? ''}`
        : '',
  });

  // Initialize an empty array to store the unit objects
  const unitObjects = [];

  dayObjects.forEach((day, index) => {
    if (day) {
      const { quantity_in_unit_of_measure, display_unit, uom_id } = day;
      const multiplier = actualMenuDays[(index + 1).toString()];

      // Check if the unit of measurement already exists in unitObjects
      const existingUnitObject = unitObjects.find((obj) => obj.unit === display_unit);

      if (existingUnitObject) {
        // If the unit exists, update its total quantity
        existingUnitObject.totalQuantity += quantity_in_unit_of_measure * multiplier;
      } else {
        // If the unit doesn't exist, create a new object for it
        const newUnitObject = {
          unit: display_unit,
          totalQuantity: quantity_in_unit_of_measure * multiplier,
          uom_id: uom_id,
        };
        unitObjects.push(newUnitObject);
      }
    }
  });

  tempMenuData.unshift({
    menuName: `Actual Menu (${actualMenu.name})`,
    menuBreakdown: formattedMenuData,
    seperatedUnits: unitObjects,
    total: { total: 0, displayUnit: undefined, uomId: undefined },
    days: days,
    type: 'Main Menu',
  });

  // Convert all units to the same unit ================================================================================
  const allUnits = tempMenuData.reduce((acc, cur) => {
    acc.push(...cur.seperatedUnits);
    return acc;
  }, []);

  let allConversions = [];
  let baseFrac;
  let total = { total: 0, displayUnit: null, uomId: null };
  if (conversions.length) {
    for (const total of allUnits) {
      const unitsReached = new Set([total.uom_id]);
      let unreached = conversions;
      let conversionTable = [];

      // Keep track of the units we can convert to
      conversionTable.push({
        from: total.uom_id,
        to: total.uom_id,
        frac: 1,
      });

      convertUnits(total.uom_id);
      function convertUnits(unit) {
        // Find the conversion of the current unit.
        const currentConversion = conversionTable.find((obj) => obj.to === unit);
        const nextToExplore = new Set();

        unreached = unreached.reduce((result, item) => {
          // If we can already convert to both units just ignore the entry.
          if (!unitsReached.has(item.from_unit_of_measure) || !unitsReached.has(item.to_unit_of_measure)) {
            if (item.from_unit_of_measure === unit) {
              unitsReached.add(item.to_unit_of_measure);
              nextToExplore.add(item.to_unit_of_measure);
              conversionTable.push({
                from: total.uom_id,
                to: item.to_unit_of_measure,
                frac: currentConversion.frac / (item.from_conversion_fraction / item.to_conversion_fraction),
              });
            } else if (item.to_unit_of_measure === unit) {
              unitsReached.add(item.from_unit_of_measure);
              nextToExplore.add(item.from_unit_of_measure);
              conversionTable.push({
                from: total.uom_id,
                to: item.from_unit_of_measure,
                frac: currentConversion.frac / (item.to_conversion_fraction / item.from_conversion_fraction),
              });
            }
            // We can't reach this unit yet.
            else {
              result.push(item);
            }
          }
          return result;
        }, []);

        // Convert all of the new units
        for (const next of nextToExplore) convertUnits(next);
      }

      total.conversions = conversionTable ?? [];
      allConversions = allConversions.concat(conversionTable ?? []);

      if (commonUnits.size) {
        commonUnits = commonUnits.intersection(new Set(conversionTable.map((obj) => obj.to)));
      }
    }
    commonUnits = [...commonUnits];

    if (commonUnits.length && conversions.length && allUnits.length) {
      baseFrac = allUnits[0].conversions.find((obj) => obj.to === commonUnits[0]);

      total.uomId = baseFrac.from;
      total.displayUnit = allUnits[0].unit;

      for (const menu of tempMenuData) {
        if (menu.seperatedUnits.length) {
          let menuTotal = 0;

          for (let i = 0; i < menu.seperatedUnits.length; i++) {
            const conversionFrac = menu.seperatedUnits[i].conversions.find((obj) => obj.to === baseFrac.to);
            menuTotal += (menu.seperatedUnits[i].totalQuantity * conversionFrac.frac) / baseFrac.frac;
          }

          menu.total = { uomId: total.uomId, displayUnit: total.displayUnit, total: menuTotal };

          if (menu.type !== 'SKU Exception') {
            total.total +=
              menuTotal * (menu.type === 'Learner Reduction' ? menu.learnerCount : currentSchool.learner_count);
          }
        }
      }
    }
  }

  // Convert all skus to the same unit =================================================================================
  const skuBreakdowns = skus
    .map((obj, index) => {
      const conversionFrac = allConversions.find(
        (con) => con.from === baseFrac?.from && obj.unit_of_measure_id === con.to
      );

      return {
        id: index,
        display: `${obj.display_name} ${obj.quantity_in_unit_of_measure}${obj.display_unit}`,
        quantity: null,
        totalInUom: null,
        ammountInBaseUnit: obj.quantity_in_unit_of_measure / conversionFrac?.frac,
      };
    })
    .sort((a, b) => a.ammountInBaseUnit < b.ammountInBaseUnit);

  let remaining = total.total;
  let totalToBuy = 0;
  for (let i = 0; i < skuBreakdowns.length; i++) {
    let qty = Math.floor(remaining / skuBreakdowns[i].ammountInBaseUnit);

    if (i === skuBreakdowns.length - 1 && remaining % skuBreakdowns[i].ammountInBaseUnit !== 0) {
      qty = Math.ceil(remaining / skuBreakdowns[i].ammountInBaseUnit);
    }

    skuBreakdowns[i].quantity = qty;
    skuBreakdowns[i].totalInUom = `${formatNumber(skuBreakdowns[i].ammountInBaseUnit * qty, 3)}${total?.displayUnit}`;

    remaining = remaining - skuBreakdowns[i].ammountInBaseUnit * qty;
    totalToBuy += skuBreakdowns[i].ammountInBaseUnit * qty;
  }

  return (
    <Fragment>
      <Breadcrumb icon={<Report />} title="Details" parent="Delivery Runs" />
      <Helmet>
        <title>PFSA | Reports | Delivery Run Summaries | Details</title>
      </Helmet>

      <div className="sticky-top" style={{ top: '80px', height: '80px' }}>
        <Paper elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" flexGrow={1}>
            <FormControl sx={{ width: '50%' }}>
              <InputLabel id="shoolLabel">School</InputLabel>
              <Select
                labelId="schoolLabel"
                id="school"
                value={currentSchoolId}
                onChange={handleSchoolChange}
                label="School"
              >
                <MenuItem value={''}>- Select -</MenuItem>
                {schools.map((val, i) => (
                  <MenuItem key={i} value={val.id}>{`${val.display_name}`}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => stepSchool('prev')}
                disabled={schools.findIndex((obj) => obj.id === currentSchoolId) === 0 || schools.length === 0}
              >
                Previous
              </button>
            </div>
            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => stepSchool('next')}
                disabled={schools.findIndex((obj) => obj.id === currentSchoolId) === schools.length - 1}
              >
                Next
              </button>
            </div>

            <div className="vr"></div>

            <FormControl sx={{ width: '50%' }}>
              <InputLabel id="skuLabel">SKU Type</InputLabel>
              <Select
                labelId="skuLabel"
                id="sku"
                value={currentSkuTypeId}
                onChange={handleSkuTypeChange}
                label="SKU Type"
              >
                <MenuItem value={''}>- Select -</MenuItem>
                {skuTypes.map((val, i) => (
                  <MenuItem key={i} value={val.id}>{`${val.skuType}`}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => stepSku('prev')}
                disabled={skuTypes.findIndex((obj) => obj.id === currentSkuTypeId) === 0 || skuTypes.length === 0}
              >
                Previous
              </button>
            </div>
            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => stepSku('next')}
                disabled={skuTypes.findIndex((obj) => obj.id === currentSkuTypeId) === skuTypes.length - 1}
              >
                Next
              </button>
            </div>
          </Stack>

          <hr />
        </Paper>
      </div>
      {!currentSchoolId && (
        <>
          <Box sx={{ width: '100%' }}>
            <Alert className="d-flex gap-3" color="secondary">
              <AlertCircle /> <p>Select a School for this delivery run to view its details.</p>
            </Alert>
          </Box>
        </>
      )}

      {currentSchoolId && (
        <>
          <div className="container" style={{ marginLeft: 0 }}>
            <div className="row">
              <div className="col-2" style={{ paddingLeft: 0 }}>
                <Typography>Actual Menu:</Typography>
              </div>
              <div className="col-10">
                <Typography>
                  {actualMenu.name} ({formatDateWithoutTime(new Date(deliveryRunDetails.consumption_start_date))} to{' '}
                  {formatDateWithoutTime(new Date(deliveryRunDetails.consumption_end_date))})
                </Typography>
              </div>
            </div>
          </div>

          {exceptions
            .filter((obj) => obj.type === 'TEMPORARY_MENU')
            .map((obj, index) => {
              return (
                <div key={index} className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-2" style={{ paddingLeft: 0 }}>
                      <Typography>Exception Menu:</Typography>
                    </div>
                    <div className="col-10">
                      <Typography sx={{ color: 'orange' }}>
                        {obj.menu} ({formatDateWithoutTime(new Date(obj.from_date))} to{' '}
                        {formatDateWithoutTime(new Date(obj.to_date))})
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            })}
          <br />

          <Typography variant="h6">SKU Type Exceptions</Typography>
          <br />
          <Box sx={{ width: '50%' }}>
            <DataGrid
              autoHeight
              hideFooter
              rows={exceptions
                .filter((obj) => obj.type === 'MENU_SKU_TYPES')
                .map((obj, i) => {
                  return { ...obj, id: i };
                })}
              columns={exceptionColumns}
            />
          </Box>

          <br />
          <hr />

          {!currentSkuTypeId && (
            <>
              <Box sx={{ width: '100%' }}>
                <Alert className="d-flex gap-3" color="secondary">
                  <AlertCircle /> <p>Select a SKU Type to view its details.</p>
                </Alert>
              </Box>
            </>
          )}

          {/*== Menus =================================================================*/}
          {currentSkuTypeId && (
            <>
              {tempMenuData.map((obj, key) => {
                return (
                  <Box key={key} sx={{ width: '50%' }}>
                    <br />
                    <Typography variant="h6">{obj.menuName}</Typography>
                    <br />
                    <DataGrid autoHeight hideFooter rows={obj.menuBreakdown} columns={menuBreakdownColumns} />
                    <br />
                    <div className="container" style={{ marginLeft: 0 }}>
                      <div className="row">
                        <div className="col-4" style={{ paddingLeft: 0 }}>
                          <Typography>Total Portion Size Per Learner</Typography>
                        </div>
                        <div className="col-8">
                          <Typography>
                            {formatNumber(obj.total.total)} {obj.total.displayUnit}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="container" style={{ marginLeft: 0 }}>
                      <div className="row">
                        <div className="col-4" style={{ paddingLeft: 0 }}>
                          <Typography>Learner Count</Typography>
                        </div>
                        <div className="col-8">
                          <Typography>
                            {obj.type === 'Learner Reduction' ? obj.learnerCount : currentSchool.learner_count}
                          </Typography>
                        </div>
                      </div>
                    </div>
                    <div className="container" style={{ marginLeft: 0 }}>
                      <div className="row">
                        <div className="col-4" style={{ paddingLeft: 0 }}>
                          <Typography>Total Portions for Period</Typography>
                        </div>
                        <div className="col-8">
                          <Typography>
                            {formatNumber(
                              obj.total.total *
                                (obj.type === 'Learner Reduction' ? obj.learnerCount : currentSchool.learner_count),
                              3
                            )}{' '}
                            {obj.total.displayUnit}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </Box>
                );
              })}
              <Box sx={{ width: '50%' }}>
                <br />
                <Typography variant="h6">Total UOM Requirements</Typography>
                <br />
                <DataGrid autoHeight hideFooter rows={skuBreakdowns} columns={skuBreakdown} />
                <br />
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-4" style={{ paddingLeft: 0 }}>
                      <Typography>Actual UoM Quantity</Typography>
                    </div>
                    <div className="col-8">
                      <Typography>
                        {formatNumber(totalToBuy, 3)} {total.displayUnit}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-4" style={{ paddingLeft: 0 }}>
                      <Typography>Target UoM Quantity</Typography>
                    </div>
                    <div className="col-8">
                      <Typography>
                        {formatNumber(total.total, 3)} {total.displayUnit}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-4" style={{ paddingLeft: 0 }}>
                      <Typography>Actual/Target Difference</Typography>
                    </div>
                    <div className="col-8">
                      <Typography>
                        {formatNumber(Math.abs(total.total - totalToBuy), 3)} {total.displayUnit}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Box>
            </>
          )}
        </>
      )}

      <Box sx={{ width: '100%' }}>
        <a href={`/delivery-runs/edit/${deliveryRunId}`} className="btn btn-secondary me-1 mt-1" role="button">
          View Delivery Run
        </a>
      </Box>
    </Fragment>
  );
};

function round(value, decimals) {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

function formatNumber(x) {
  var parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
}

const exceptionColumns = [
  {
    field: 'exception_id',
    headerName: '',
    width: 90,
    align: 'center',
    renderCell: (params) => (
      <Link href={`/exceptions/edit/${params.value}`} target="_blank" rel="noopener">
        <VisibilityIcon />
      </Link>
    ),
  },
  {
    field: 'sku_type',
    headerName: 'SKU',
    editable: false,
    flex: 1,
  },
  {
    field: 'from_date',
    headerName: 'From',
    editable: false,
    flex: 1,
    renderCell: (params) => formatDateWithoutTime(params.value),
  },
  {
    field: 'to_date',
    headerName: 'To',
    editable: false,
    flex: 1,
    renderCell: (params) => formatDateWithoutTime(params.value),
  },
];

const menuBreakdownColumns = [
  {
    field: 'rowName',
    headerName: '',
    editable: false,
    flex: 2,
  },
  {
    field: 'monday',
    headerName: 'Mo',
    editable: false,
    flex: 1,
  },
  {
    field: 'tuesday',
    headerName: 'Tu',
    editable: false,
    flex: 1,
  },
  {
    field: 'wednesday',
    headerName: 'We',
    editable: false,
    flex: 1,
  },
  {
    field: 'thursday',
    headerName: 'Th',
    editable: false,
    flex: 1,
  },
  {
    field: 'friday',
    headerName: 'Fr',
    editable: false,
    flex: 1,
  },
];

const skuBreakdown = [
  {
    field: 'display',
    headerName: 'SKU Type',
    editable: false,
    flex: 1,
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    editable: false,
    flex: 1,
    align: 'right',
    renderCell: (params) => formatNumber(params.value, 3),
  },
  {
    field: 'totalInUom',
    headerName: 'Total Quantity',
    editable: false,
    flex: 1,
    align: 'right',
  },
];

export default DeliveryRunDetails;
